import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, formatDateTime } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { Megaphone, AlertTriangle, Check, Pencil, X, History } from "lucide-react";

const ANN_TYPES = [
  { key: "alerta", label: "🔴 Alerta", type: "alert" },
  { key: "atencao", label: "🟡 Atenção", type: "warning" },
  { key: "informativo", label: "🟢 Informativo", type: "normal" },
  { key: "comunicado", label: "📢 Comunicado", type: "normal" },
  { key: "evento", label: "🎉 Evento", type: "normal" },
  { key: "manutencao", label: "🔧 Manutenção", type: "normal" },
  { key: "seguranca", label: "🛡️ Segurança", type: "normal" },
];
const RADAR_OPTS = ["administracao", "agua", "energia", "garagem", "elevadores", "eventos", "lazer", "manutencoes", "seguranca", "pets", "encomendas", "emergencia"];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const s = new Date(iso).toLocaleString("sv-SE", { timeZone: "America/Sao_Paulo" });
  return s.replace(" ", "T").slice(0, 16);
};

const emptyAlert = { title: "", message: "", category: "emergencia", towers: "all", resolve_at: "", auto_resolve: false };

export default function Communication() {
  const qc = useQueryClient();
  const [mode, setMode] = useState("comunicado");
  const { data: alerts, isLoading } = useQuery({ queryKey: ["mgr-alerts"], queryFn: async () => (await api.get("/alerts")).data });
  const { data: resolved } = useQuery({ queryKey: ["mgr-alerts-resolved"], queryFn: async () => (await api.get("/alerts", { params: { status: "resolved" } })).data });

  const [ann, setAnn] = useState({ typeKey: "comunicado", title: "", message: "", category: "administracao", tower: "all", pinned: false });
  const [alert, setAlert] = useState(emptyAlert);
  const [editingAlertId, setEditingAlertId] = useState(null);
  const [saving, setSaving] = useState(false);

  const refreshAlerts = () => {
    qc.invalidateQueries({ queryKey: ["mgr-alerts"] });
    qc.invalidateQueries({ queryKey: ["mgr-alerts-resolved"] });
  };

  const publishAnn = async () => {
    if (!ann.title.trim() || !ann.message.trim()) return toast.error("Preencha título e mensagem.");
    setSaving(true);
    try {
      const t = ANN_TYPES.find((x) => x.key === ann.typeKey);
      if (ann.typeKey === "alerta" || ann.typeKey === "atencao") {
        await api.post("/alerts", { type: t.type, title: ann.title, message: ann.message, towers: [ann.tower], category: ann.category, pinned: ann.pinned });
      } else {
        await api.post("/announcements", { type: ann.typeKey, title: ann.title, message: ann.message, tower: ann.tower, category: ann.category, pinned: ann.pinned });
      }
      setAnn({ typeKey: "comunicado", title: "", message: "", category: "administracao", tower: "all", pinned: false });
      refreshAlerts();
      toast.success("Comunicado publicado!");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const publishAlert = async () => {
    if (!alert.title.trim() || !alert.message.trim()) return toast.error("Preencha os campos.");
    setSaving(true);
    try {
      const payload = { type: "alert", title: alert.title, message: alert.message, towers: [alert.towers], category: alert.category, pinned: true, resolve_at: alert.resolve_at || null, auto_resolve: alert.auto_resolve };
      if (editingAlertId) await api.put(`/alerts/${editingAlertId}`, payload);
      else await api.post("/alerts", payload);
      setAlert(emptyAlert);
      setEditingAlertId(null);
      refreshAlerts();
      toast.success(editingAlertId ? "Alerta atualizado!" : "Alerta publicado na Home dos moradores!");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resolveAlert = async (id) => {
    await api.post(`/alerts/${id}/resolve`);
    refreshAlerts();
    toast.success("Alerta resolvido. Permanece no histórico.");
  };

  const startEditAlert = (a) => {
    setMode("alerta");
    setEditingAlertId(a.id);
    setAlert({ title: a.title, message: a.message, category: a.category || "emergencia", towers: (a.towers && a.towers[0]) || "all", resolve_at: toLocalInput(a.resolve_at), auto_resolve: !!a.auto_resolve });
  };
  const cancelEdit = () => { setEditingAlertId(null); setAlert(emptyAlert); };

  const inputCls = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Central de comunicação</h1>
        <p className="text-sm text-muted-foreground">Fale com todo o condomínio em um só lugar.</p>
      </div>

      <div className="flex gap-2 glass rounded-full p-1 max-w-md">
        <button onClick={() => setMode("comunicado")} data-testid="mode-comunicado" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${mode === "comunicado" ? "brand-gradient text-white" : "text-muted-foreground"}`}><Megaphone className="w-4 h-4" /> Novo comunicado</button>
        <button onClick={() => setMode("alerta")} data-testid="mode-alerta" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${mode === "alerta" ? "bg-red-500 text-white" : "text-muted-foreground"}`}><AlertTriangle className="w-4 h-4" /> {editingAlertId ? "Editar alerta" : "Criar alerta"}</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-3xl p-6 space-y-4">
          {mode === "comunicado" ? (
            <>
              <p className="font-display font-semibold">Novo comunicado</p>
              <div className="flex flex-wrap gap-2">
                {ANN_TYPES.map((t) => (
                  <button key={t.key} onClick={() => setAnn({ ...ann, typeKey: t.key })} data-testid={`ann-type-${t.key}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ann.typeKey === t.key ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>{t.label}</button>
                ))}
              </div>
              <input value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} data-testid="ann-title" placeholder="Título" className={inputCls} />
              <textarea value={ann.message} onChange={(e) => setAnn({ ...ann, message: e.target.value })} data-testid="ann-message" rows={4} placeholder="Mensagem" className={`${inputCls} resize-none`} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Público (torre)</label>
                  <input value={ann.tower} onChange={(e) => setAnn({ ...ann, tower: e.target.value })} data-testid="ann-tower" placeholder="all ou Torre 2" className={inputCls} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Categoria (Radar)</label>
                  <select value={ann.category} onChange={(e) => setAnn({ ...ann, category: e.target.value })} data-testid="ann-category" className={inputCls}>
                    {RADAR_OPTS.map((r) => <option key={r} value={r} className="bg-background">{r}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ann.pinned} onChange={(e) => setAnn({ ...ann, pinned: e.target.checked })} data-testid="ann-pin" className="accent-purple-500" /> Fixar na Home</label>
              <button onClick={publishAnn} disabled={saving} data-testid="ann-submit" className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform disabled:opacity-60">Publicar comunicado</button>
            </>
          ) : (
            <>
              <p className="font-display font-semibold text-red-400 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> {editingAlertId ? "Editar alerta" : "Alerta / Situação agora"}</p>
              <p className="text-xs text-muted-foreground">Aparece imediatamente no dashboard (Situação agora) dos moradores afetados.</p>
              <input value={alert.title} onChange={(e) => setAlert({ ...alert, title: e.target.value })} data-testid="alert-title" placeholder="Ex: Falta de água — Torre B" className={inputCls} />
              <textarea value={alert.message} onChange={(e) => setAlert({ ...alert, message: e.target.value })} data-testid="alert-message" rows={3} placeholder="Descreva a situação e a previsão de normalização" className={`${inputCls} resize-none`} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Torres afetadas</label>
                  <input value={alert.towers} onChange={(e) => setAlert({ ...alert, towers: e.target.value })} data-testid="alert-towers" placeholder="all ou Torre 2" className={inputCls} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Categoria (Radar)</label>
                  <select value={alert.category} onChange={(e) => setAlert({ ...alert, category: e.target.value })} data-testid="alert-category" className={inputCls}>
                    {RADAR_OPTS.map((r) => <option key={r} value={r} className="bg-background">{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Previsão de resolução (opcional)</label>
                <input type="datetime-local" value={alert.resolve_at} onChange={(e) => setAlert({ ...alert, resolve_at: e.target.value })} data-testid="alert-resolve-at" className={inputCls} />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={alert.auto_resolve} onChange={(e) => setAlert({ ...alert, auto_resolve: e.target.checked })} data-testid="alert-auto-resolve" className="accent-purple-500" /> Resolver automaticamente no horário previsto</label>
              <div className="flex gap-2">
                <button onClick={publishAlert} disabled={saving} data-testid="alert-submit" className="flex-1 rounded-full bg-red-500 hover:bg-red-600 py-3 text-white font-semibold active:scale-95 transition-all disabled:opacity-60">{editingAlertId ? "Salvar alterações" : "Publicar alerta"}</button>
                {editingAlertId && <button onClick={cancelEdit} data-testid="alert-cancel-edit" className="rounded-full bg-white/5 border border-white/10 px-4 text-sm font-medium">Cancelar</button>}
              </div>
            </>
          )}
        </div>

        <div className="space-y-3">
          <p className="font-display font-semibold">Situação agora (ativos)</p>
          {isLoading ? <Loading /> : !alerts?.length ? <EmptyState icon={Megaphone} title="Nenhum alerta ativo" subtitle="🟢 Tudo funcionando normalmente." /> :
            alerts.map((a) => (
              <div key={a.id} data-testid="active-alert" className="glass rounded-2xl p-4 flex items-start gap-3">
                <span className="text-lg">{a.type === "alert" ? "🔴" : a.type === "warning" ? "🟡" : "🟢"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.message}</p>
                  {a.resolve_at && <p className="text-[11px] text-primary mt-1">⏱ Previsão: {formatDateTime(a.resolve_at)}{a.auto_resolve ? " · auto" : ""}</p>}
                </div>
                <button onClick={() => startEditAlert(a)} data-testid="edit-alert" className="p-1.5 rounded-full hover:bg-white/10 text-muted-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => resolveAlert(a.id)} data-testid="resolve-alert" className="p-1.5 rounded-full hover:bg-emerald-500/10 text-emerald-400 transition-colors" title="Marcar como resolvido"><Check className="w-4 h-4" /></button>
              </div>
            ))}

          <p className="font-display font-semibold pt-3 flex items-center gap-2"><History className="w-4 h-4 text-muted-foreground" /> Histórico de alertas</p>
          {!resolved?.length ? <p className="text-xs text-muted-foreground">Nenhum alerta resolvido ainda.</p> :
            resolved.map((a) => (
              <div key={a.id} data-testid="resolved-alert" className="glass rounded-2xl p-3 flex items-start gap-3 opacity-80">
                <span className="text-base">🟢</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{a.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Resolvido {formatDateTime(a.resolved_at)} · {a.resolved_auto ? "automático" : "manual"}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
